import React, { useCallback, useEffect, useState } from 'react';
import '@chewy/kib-choices-styles/dist/styles/kib-switch.css';
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import '@chewy/kib-fields-styles/dist/styles/kib-input-text.css';
import '@chewy/kib-messaging-styles/dist/styles/kib-snackbar-manager.css';
import '@chewy/kib-messaging-styles/dist/styles/kib-snackbar.css';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibSwitch } from '@chewy/kib-choices-react';
import { KibSnackbarManager, KibSnackbar } from '@chewy/kib-messaging-react';
import { ApiPetProfile } from '@chewy/pet-profile-api-client';
import PetTypeSelectorCards, {
  CreatePetProfileSheet,
  PetTypeData,
  PetTypeSelectorStyles,
  NewPetProfileSheetData,
} from '@chewy/pet-profile-ux-components';
import { isAuth } from '../../util/auth';

export const CreatePetProfileSheetSection = () => {
  const strings = {
    createPetProfileSheetTitle: 'Create Pet Profile Sheet',
    notLoggedIn: 'Not logged in.',
    sheetButton: 'Show Create Pet Profile Sheet',
    populatedDataTitle: 'Populated Pet Profile Sheet Data',
    petDataFetchedLabel: 'Pet Type Data is already fetched',
    isOnePetFormLabel: 'Is one pet form',
  };

  const [loadedPetTypeData, setLoadedPetTypeData] = useState<Array<PetTypeData>>([]);
  const [preloadedPetTypeData, setPreloadedPetTypeData] = useState<Array<PetTypeData>>([]);
  const [showCreatePetProfileSheet, setShowCreatePetProfileSheet] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isPetTypeDataPreloaded, setIsPetTypeDataPreloaded] = useState(false);
  const [isOnePageForm, setIsOnePageForm] = useState(false);
  const [newPetProfileData, setNewPetProfileData] = useState<NewPetProfileSheetData | undefined>();

  const onLoaded = useCallback((petTypeData: Array<PetTypeData>) => {
    setLoadedPetTypeData(petTypeData);
    setIsButtonDisabled(false);
  }, []);

  const onLoadError = useCallback((error: any) => {
    setSnackbarMessage(`An error occurred: ${error}.`);
    setShowSnackbar(true);
  }, []);

  const onSelection = useCallback(
    (petTypeId: number, petTypeName: string, avatarImgUrl: string) => {
      setNewPetProfileData((prevValue) => {
        let newPPD: NewPetProfileSheetData = {
          name: prevValue?.name,
          petType: {
            typeName: petTypeName,
            typeId: petTypeId,
            avatarImgUrl: avatarImgUrl,
          },
        };
        return newPPD;
      });
    },
    [setNewPetProfileData],
  );

  const onCreated = useCallback((response: ApiPetProfile) => {
    setShowCreatePetProfileSheet(false);
    setSnackbarMessage(`Pet Profile Created for ${response.name}.`);
    setShowSnackbar(true);
  }, []);

  const onSheetClose = useCallback(() => {
    setShowCreatePetProfileSheet(false);
  }, []);

  useEffect(() => {
    if (isPetTypeDataPreloaded) {
      setPreloadedPetTypeData(loadedPetTypeData);
    } else {
      setPreloadedPetTypeData([]);
    }
  }, [isPetTypeDataPreloaded, loadedPetTypeData]);

  const getUnauthenticatedState = () => (
    <div className="section">
      <h6>{strings.notLoggedIn}</h6>
    </div>
  );

  const getAuthenticatedState = () => (
    <div className="section">
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
        }}
      >
        <div className="section">
          <KibButtonNew
            theme="action"
            onClick={() => setShowCreatePetProfileSheet(true)}
            disabled={isButtonDisabled}
          >
            {strings.sheetButton}
          </KibButtonNew>
        </div>

        <hr />
        <div className="section">
          <h5>{strings.populatedDataTitle}</h5>
          <KibSwitch
            label={strings.petDataFetchedLabel}
            selectedValue={isPetTypeDataPreloaded}
            onChange={(newValue) => setIsPetTypeDataPreloaded(newValue)}
          />
          <KibSwitch
            label={strings.isOnePetFormLabel}
            selectedValue={isOnePageForm}
            onChange={(newValue) => setIsOnePageForm(newValue)}
          />
          <div className="hidden">
            <PetTypeSelectorCards
              pickerName="create-pet-profile-sheet-selected-type"
              style={PetTypeSelectorStyles.list}
              onLoaded={onLoaded}
              onSelection={onSelection}
              onLoadError={onLoadError}
            />
          </div>
        </div>
      </form>
      <div className="section">
        <CreatePetProfileSheet
          sheetName="create-pet-profile-sheet"
          newPetProfileData={newPetProfileData}
          show={showCreatePetProfileSheet}
          onClose={onSheetClose}
          onCreated={onCreated}
          petTypeData={preloadedPetTypeData}
          isOnePageForm={isOnePageForm}
        />

        <KibSnackbarManager
          visible={showSnackbar}
          onClose={() => setShowSnackbar(false)}
          timeout={5000}
        >
          <KibSnackbar>{snackbarMessage}</KibSnackbar>
        </KibSnackbarManager>
      </div>
    </div>
  );

  return (
    <div className="component-card" key="create-pet-profile-sheet">
      <h2>{strings.createPetProfileSheetTitle}</h2>
      <hr />
      {isAuth() ? getAuthenticatedState() : getUnauthenticatedState()}
    </div>
  );
};
